import * as React from "react";
import { observer, inject } from "mobx-react";
import {Container, Modal, Button, Col, Row, Spinner, Table} from "react-bootstrap";
import {Committee, CommitteeStore} from "Stores/committeeStore";
import {Defaults, Utils} from "../../common/utils";
import {User, UserStore} from "Stores/userStore";
import {
    Column,
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    useReactTable
} from "@tanstack/react-table";
import {SimpleCheckbox} from "Components/Common/SimpleCheckbox";
import * as _ from "lodash";
import {TableParams} from "Stores/commonStore";
import DebouncedInput from "Components/Common/DebouncedInput";

interface UsersFormModalState {
    showCurrent: boolean
    usersFilter: string
    showFilters: boolean
    pageNumber: number
    pages: number
    pageSize: number
    loading: boolean
    users: User[]
    selected: User[]
    saved: User[]
}

interface UsersFormModalProps {
    committee?: Committee
    handleClose: () => void
    handleSubmit: () => void
    userStore?: UserStore
    committeeStore?: CommitteeStore

}

@inject("userStore", "committeeStore")
@observer
export class UsersFormModal extends React.Component<UsersFormModalProps, UsersFormModalState> {
    defaultPage = Defaults.tablePage();
    defaultPageSize = Defaults.tablePageSize();

    state = {
        showCurrent: true,
        usersFilter: "",
        showFilters: false,
        pages: 0,
        pageSize: Defaults.tablePageSize(),
        pageNumber: Defaults.tablePage(),
        loading: false,
        users: [] as User[],
        selected: [] as User[],
        saved: [] as User[]
    };

    filtering = false;
    reactTableRefAll: any;
    reactTableRefCurrent: any;

    getCurrentColumns = () => {
        const columns: ColumnDef<User>[] = [
            {
                id: "selector",
                size: 40,
                cell: props => {
                    const onChange = () => {
                        this.onSelect(props.row.original);
                    };

                    return (
                        <SimpleCheckbox
                            isChecked={this.isRowSelected(props.row.original)}
                            onChange={onChange}
                            id={`committees-users-${props.row.original.ruuid}`} />
                    );
                }
            },
            {
                header: "First Name",
                accessorKey: "first_name",
            },
            {
                header: "Last Name",
                accessorKey: "last_name",
            },
            {
                header: "Email",
                accessorKey: "email",
            }
        ];

        return columns;
    };

    getAllColumns = () => {
        const columns: ColumnDef<User>[] = [
            {
                id: "selector",
                size: 40,
                cell: props => {
                    const onChange = () => {
                        this.onSelect(props.row.original);
                    };

                    return (
                        <SimpleCheckbox
                            isChecked={this.isRowSelected(props.row.original)}
                            onChange={onChange}
                            id={`committees-users-${props.row.original.ruuid}`} />
                    );
                }
            },
            {
                header: "First Name",
                accessorKey: "first_name",
            },
            {
                header: "Last Name",
                accessorKey: "last_name",
            },
            {
                header: "Email",
                accessorKey: "email",
            }
        ];

        return columns;
    };

    getColumns = () => {
        if (this.state.showCurrent) {
            return this.getCurrentColumns();
        }

        return this.getAllColumns();
    };

    isRowSelected = (row: User): boolean => {
        const found = this.state.selected.find((user: User) => user.ruuid === row.ruuid);
        return !!found;
    };

    resetPage = () => {
        if (this.state.pageNumber !== this.defaultPage) {
            this.setState({ pageNumber: this.defaultPage});
        }
    };

    onSelect = (row: User) => {
        let selected = this.state.selected.slice();
        if (!this.isRowSelected(row)) {
            selected.push(row);
        } else {
            selected = this.state.selected.filter((user: User) => user.ruuid !== row.ruuid);
        }
        this.setState({selected});
    };

    handleClose = () => this.props.handleClose();
    handleSubmit = () => {
        if (this.props.committeeStore && this.props.committee) {
            const selected = this.state.selected.map(user => user.ruuid);
            const current = this.state.saved.map(user => user.ruuid);
            const adds = _.difference(selected, current);
            const removes = _.difference(current, selected);
            this.props.committeeStore.updateFacultiesForCommittee(this.props.committee.id, adds, removes).then(() => {
                this.props.handleSubmit();
            });
        }
    };

    handleShowCurrent = () => {
        if (!this.state.showCurrent) {
            const showFilters = this.state.saved.length !== 0;
            this.setState({showCurrent: true, users: this.state.saved.slice(), showFilters});
        }
    };

    handleHideCurrent = () => {
        if (!this.props.userStore) {
            return null;
        }
        if (this.state.showCurrent) {
            this.setState({
                showCurrent: false,
                users: this.props.userStore.users.slice(),
                showFilters: true
            });
        }
    };

    componentDidMount() {
        if (this.props.userStore && this.props.committee) {
            this.setState({loading: true});
            this.props.userStore.fetchUserListAsync({pageSize: Defaults.tablePageSize(), page: 1});
            this.props.userStore.fetchUsersForCommittee(this.props.committee.id).then((users: User[]) => {
                if (!users) {
                    return null;
                }
                const showFilters = users.length !== 0;
                this.setState({users: users, selected: users, saved: users, loading: false, showFilters});
            });
        }
    }

    getTotalRecords = () => {
        if (!this.props.userStore) {
            return 0;
        }

        return this.props.userStore.totalUsers || 0;
    };

    getNumberOfPages = () => {
        return Math.ceil(this.getTotalRecords() / this.state.pageSize) || 0;
    };

    handlePageChange = (pageNumber: number) => {
        this.setState({ pageNumber });
    };

    onFilteredChange = () => {
        this.filtering = true;
    };

    fetchData = (state) => {
        if (this.state.showCurrent) {
            return;
        }
        if (!this.props.userStore) {
            return;
        }

        let pageNumber = this.state.pageNumber;
        if (this.filtering) {
            pageNumber = 0;
            this.filtering = false;
        }
        this.setState({loading: false});
        let sortBy;
        let sortOrder;
        if (state.sorted && state.sorted.length > 0) {
            sortBy = state.sorted[0].id;
            sortOrder = state.sorted[0].desc ? "desc" : "asc";
        }

        let filters;
        if (state.filtered && state.filtered.length > 0) {
            filters = {};
            state.filtered.forEach(filter => {
               filters[filter.id] = { operator: "contains", value: filter.value };
            });
        }

        const args: TableParams = {pageSize: this.state.pageSize, page: pageNumber + 1, sortBy: sortBy, sortOrder: sortOrder, filters: filters};
        this.props.userStore.fetchUserListAsync(args).then(() => {
            if (!this.props.userStore) {
                this.setState({
                    pages: this.getNumberOfPages(),
                    pageNumber: pageNumber,
                    loading: false
                });
                return null;
            }
            this.setState({
                pages: this.getNumberOfPages(),
                pageNumber: pageNumber,
                loading: false,
                users: this.props.userStore.users.slice()
            });
        });
    };

    fetchDataWithDebounce = _.debounce(this.fetchData, 250);

    fetchStrategy = (state) => {
        if (this.filtering) {
            return this.fetchDataWithDebounce(state);
        } else {
            return this.fetchData(state);
        }
    };

    showRange = () => {
        if (this.getTotalRecords() === 0) return "";

        const current = (this.state.pageSize * (this.state.pageNumber + 1)) - this.state.pageSize + 1;
        const end = this.state.pageSize * (this.state.pageNumber + 1) > this.getTotalRecords()
            ? this.getTotalRecords()
            : this.state.pageSize * (this.state.pageNumber + 1);
        const total = this.getTotalRecords();

        return ` ${current} - ${end} of ${total} `;
    };

    pageBackward = () => {
        this.setState({ pageNumber: this.state.pageNumber - 1});
    };

    pageForward = () => {
        this.setState({ pageNumber: this.state.pageNumber + 1});
    };

    renderPagination = () => {
        if (!this.state.showCurrent) {
            return (
                <Row className={"mb-2 mt-2"}>
                    <Col>
                        <div className="center">
                            <button onClick={this.pageBackward} className="back"
                                    data-test-id="committee-users-page-backward"
                                    disabled={this.state.loading || this.state.pageNumber <= 0}>
                                <i className="icon icon-angle-left"/>
                            </button>
                            <span className="range">{this.showRange()}</span>
                            <button onClick={this.pageForward} className="forward"
                                    data-test-id="committee-users-page-forward"
                                    disabled={this.state.loading || Utils.isNextButtonDisabled(this.state.pageNumber, this.state.pageSize, this.getTotalRecords())}>
                                <i className="icon icon-angle-right"/>
                            </button>
                        </div>
                    </Col>
                </Row>
            );
        }
    };

    renderCurrentTable = () => {
        if (!this.state.showCurrent) {
            return null;
        }

        if (!this.props.userStore) {
            return null;
        }

        return <CurrentUsersReactTable columns={this.getColumns()} data={this.state.users.slice()}/>;
    };

    renderAllTable = () => {
        if (this.state.showCurrent) {
            return null;
        }

        if (!this.props.userStore) {
            return null;
        }

        return <AllUsersReactTable columns={this.getColumns()} data={this.state.users.slice()}/>;
    };

    render() {
        if (!this.props.committee || !this.props.committeeStore) {
            return null;
        }

        return (
            <Modal centered show={true} onHide={this.handleClose} dialogClassName="modal-container wide mapping modal-committees-users">
                <Modal.Header>
                    <Container>
                        <Row>
                            <Col xs={12} lg={7} className="text-lg-start">
                                <span className="modal-title">Users</span>
                            </Col>
                            <Col xs={12} lg={3} className="actions">
                                <Button variant="outline-light" className={this.state.showCurrent ? "active" : ""}
                                        data-test-id="committee-users-current-btn"
                                        onClick={this.handleShowCurrent} >current users</Button>
                            </Col>
                            <Col xs={12} lg={2} className="actions">
                                <Button variant="outline-light" className={!this.state.showCurrent ? "active" : ""}
                                        data-test-id="committee-users-show-all-btn"
                                        disabled={this.props.committee.protected && !Utils.isSuperAdmin()}
                                        onClick={this.handleHideCurrent}>show all</Button>
                            </Col>
                        </Row>
                    </Container>
                </Modal.Header>
                <Modal.Body className={"list-container"}>
                    { this.renderPagination() }
                    <div className={"table-container"}>
                        { this.renderAllTable() }
                        { this.renderCurrentTable() }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.handleClose}>
                        Exit
                    </Button>
                    <Button
                        variant="primary"
                        disabled={this.props.committeeStore.isUpdatingFacultiesForCommittee}
                        onClick={this.handleSubmit}>
                        {this.props.committeeStore.isUpdatingFacultiesForCommittee &&
                            <Spinner animation="border" className="green-spinner" size="sm"/>} Save
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

interface CurrentUsersReactTableProps {
    columns: ColumnDef<User>[]
    data: User[]
}

function CurrentUsersReactTable({columns, data}: CurrentUsersReactTableProps) {
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        state: {
            columnFilters
        },
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel()
    });

    return (
        <Table>
            <thead>
            {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => {
                        return (
                            <th key={header.id}>
                                {header.isPlaceholder ? null : (
                                    <>
                                        <div>
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                        </div>
                                        {header.column.getCanFilter() && (
                                            <div>
                                                <Filter column={header.column} />
                                            </div>)}
                                    </>
                                )}
                            </th>
                        );
                    })}
                </tr>
            ))}
            </thead>
            <tbody>
            {table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                        <td key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                    ))}
                </tr>
            ))}
            {table.getRowModel().rows.length === 0 && (<tr><td colSpan={4} className={"text-center"}>No assigned users</td></tr>)}
            </tbody>
        </Table>
    );
}

interface AllUsersReactTableProps {
    columns: ColumnDef<User>[]
    data: User[]
}

function AllUsersReactTable({columns, data}: AllUsersReactTableProps) {
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        state: {
            columnFilters
        },
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel()
    });
    return (
        <Table>
            <thead>
            {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => {
                        return (
                            <th key={header.id}>
                                {header.isPlaceholder ? null : (
                                    <>
                                        <div>
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                        </div>
                                        {header.column.getCanFilter() && (
                                            <div>
                                                <Filter column={header.column} />
                                            </div>)}
                                    </>
                                )}
                            </th>
                        );
                    })}
                </tr>
            ))}
            </thead>
            <tbody>
            {table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                        <td key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                    ))}
                </tr>
            ))}
            {table.getRowModel().rows.length === 0 && (<tr><td colSpan={4} className={"text-center"}>No Data Found</td></tr>)}
            </tbody>
        </Table>
    );
}

function Filter({column}: { column: Column<any, unknown> }) {
    const columnFilterValue = column.getFilterValue();

    return (
        <DebouncedInput type={"text"} value={(columnFilterValue ?? "") as string} onChange={value => column.setFilterValue(value)} />
    );
}